import React from 'react'
import { connect } from 'react-redux'
import context from '@/context'
import { localized } from '@/decorators/localized'
import { actionCreators, getAvailableParents } from '@/store/OrganizationalUnits'
import { actionCreators as dialogActions } from '@/store/Dialogs'
import { actionCreators as jobPositionActions } from '@/store/JobPosition'
import Resx from '@/components/Resx'
import { Col, Row } from 'reactstrap'
import { BsrsGrid, actionButtonsColumnProps, Column, LookupCell, createLookupFilterCell, DateCell, DateFilterCell, BooleanCellStatus, BooleanFilterCell } from '@/components/BsrsGrid'
import { Main, MainTop, MainTopTitle, MainBody, WhiteBox } from '@/components/Layout'
import Icon from '@/components/Icon'
import LoadingIndicator from '@/components/LoadingIndicator'
import OrganizationalUnitForm from './form'
import { dialogTypes } from '@/dialogs'
import { NotFound404 } from '@/components/404'
import { Button } from '@/components/button'
import { getOrganizationUnitGridButtons } from './gridButtons'
import Name from '@/components/Name'
import cached from '@/internal-app/cached'
import { withPermissions } from '@/decorators/withPermissions'

@localized()
@withPermissions()
class Detail extends React.Component {
  componentDidMount () {
    this.props.getAll()
  }

  render () {
    const {
      id, unit, subItems, treeData, units, loading, localize, localizeProps,
      openDialog, create, update, deleteUnit,
      addJobPosition, removeJobPosition, jobPositionLoading
    } = this.props

    if (!loading && !unit) return <NotFound404 />
    const localizedField = localizeProps()
    const buttons = [
      { url: x => `/users/${x.userId}`, icon: 'pencil' }
    ].filter(x => this.props.isGranted('CanManageUsers'))

    const jobPositionButtons = []

    if (this.props.isGranted('CanManageUsers')) {
      jobPositionButtons.push({ url: x => `/organizational-units/${unit.id}/job-position-permissions/${x.id}`, icon: 'pencil' })
      jobPositionButtons.push({ onDelete: item => removeJobPosition(item.id) })
    }

    return <Main>
      <MainTop>
        <MainTopTitle backUrl='/organizational-units'>
          <Resx name='OrganizationalUnit.DetailTitle' /> {!loading && <span> - <Name data={unit} /> (<Name data={unit.unitType} />)</span>}
        </MainTopTitle>
      </MainTop>
      <MainBody transparent>
        {loading ? (
          <LoadingIndicator />
        ) : (
          <Row>
            <Col xl='4'>
              <WhiteBox>
                <React.Fragment>
                  {this.props.isGranted('CanManageOrganizationalUnits') && <div className='text-right'>
                    <Button icon='pencil' size='sm' color='primary' onClick={() => openDialog({
                      dialogType: dialogTypes.organizationalUnit,
                      value: unit,
                      availableParents: getAvailableParents(treeData, unit.organizationUnitId),
                      units: units,
                      onAction: update
                    })}><Resx name='Common.Edit' /></Button>
                  </div>}
                  <OrganizationalUnitForm unit={unit} readonly />
                </React.Fragment>
              </WhiteBox>
              <WhiteBox
                dataCy='JobPositionTitleBox'
                header={
                  <React.Fragment>
                    <span>{localize('JobPosition.JobPositionTitle')}</span>
                    {this.props.isGranted('CanManageUsers') && <Button color='primary' size='sm' onClick={() => openDialog({
                      dialogType: dialogTypes.jobTitle,
                      organizationalUnitId: unit.id,
                      onAction: position => addJobPosition(unit.id, position.id)
                    })}>
                      <Icon name='plus' />
                      <span><Resx name='Common.Add' /></span>
                    </Button>}
                  </React.Fragment>
                }>
                {jobPositionLoading ? <LoadingIndicator />
                  : <BsrsGrid dataSource={req => context.api.jobPositionService.getJobPositions(id, req)} simpleView>
                    <Column field='code' title={localize('Common.Code')} width='150px' />
                    <Column field={localizedField} title={localize('Common.Name')} />
                    <Column width='45px' {...actionButtonsColumnProps(jobPositionButtons)} />
                  </BsrsGrid>}
              </WhiteBox>
            </Col>
            <Col xl='8'>
              <WhiteBox
                dataCy='SubordinateUnitsBox'
                header={
                  <React.Fragment>
                    <span>{localize('OrganizationalUnit.SubordinateUnits')}</span>
                    {this.props.isGranted('CanManageOrganizationalUnits') && <Button color='primary' size='sm' onClick={() => openDialog({
                      dialogType: dialogTypes.organizationalUnit,
                      parent: unit,
                      units: units,
                      onAction: create
                    })}>
                      <Icon name='plus' />
                      <span><Resx name='Common.Add' /></span>
                    </Button>}
                  </React.Fragment>
                }>
                <BsrsGrid items={subItems} simpleView>
                  <Column field='name' title={localize('OrganizationalUnit.Name')} />
                  <Column field='reference' title={localize('OrganizationalUnit.Reference')} />
                  <Column field='code' title={localize('OrganizationalUnit.Code')} width='150px' />
                  <Column width='45px' {...getOrganizationUnitGridButtons(deleteUnit)} />
                </BsrsGrid>
              </WhiteBox>
              <WhiteBox header='OrganizationalUnit.UsersInOrganizationalGroup'>
                <BsrsGrid dataSource={(req) => context.api.organizationalUnitService.getUsersList(id, req)}>
                  <Column field='userName' title={localize('UsersManagement.UserName')} />
                  <Column field='email' title={localize('UsersManagement.EmailAddress')} />
                  <Column field='jobPosition' title={localize('OrganizationalUnit.JobPosition')} cell={LookupCell} filterCell={createLookupFilterCell(cached.getJobTitles)} />
                  <Column field='jobPositionValidFrom' title={localize('UsersManagement.JobPositionValidFrom')} cell={DateCell} filterCell={DateFilterCell} width='110px' />
                  <Column field='jobPositionValidTo' title={localize('UsersManagement.JobPositionValidTo')} cell={DateCell} filterCell={DateFilterCell} width='110px' />
                  <Column field='jobPositionActing' title={localize('Common.Acting')} cell={BooleanCellStatus} filterCell={BooleanFilterCell} width='60px' />
                  <Column {...actionButtonsColumnProps(buttons)} />
                </BsrsGrid>
              </WhiteBox>
            </Col>
          </Row>
        )}
      </MainBody>
    </Main>
  }
}

const mapStateToProps = (state, ownProps) => {
  const id = parseInt(ownProps.match.params.id, 10)
  const unit = state.organizationalUnits.units.find(x => x.id === id)
  const subItems = unit
    ? state.organizationalUnits.units.filter(x => x.organizationUnitParentId === unit.organizationUnitId)
    : []
  return {
    id,
    loading: state.organizationalUnits.loading,
    units: state.organizationalUnits.units,
    unit,
    subItems,
    treeData: state.organizationalUnits.treeData,
    jobPositionLoading: state.jobPosition.loading,
    savingPermissions: state.jobPositionPermissions.saving,
    ...ownProps
  }
}

const mapDispatchToProps = {
  getAll: actionCreators.getAll,
  create: actionCreators.create,
  update: actionCreators.update,
  deleteUnit: actionCreators.delete,
  openDialog: dialogActions.openDialog,
  addJobPosition: jobPositionActions.add,
  removeJobPosition: jobPositionActions.remove
}

export default connect(mapStateToProps, mapDispatchToProps)(Detail)



// WEBPACK FOOTER //
// ./src/internal-app/pages/organizationalUnits/detail.js